function mod(a, b) {
	return ((a % b) + b) % b;
}

// Three-channel Dense output is stored as [left, mono, right]. The scope's
// existing stereo drawing uses the two outer channels.
function toStereo(value) {
	return value?.length >= 3 ? [value[0], value[2]] : value;
}

export class Scope {
	constructor() {
		this.analyser = [null, null];
		this.analyserData = [null, null];
		this.canvasContainer = null;
		this.canvasCtx = null;
		this.canvasElem = null;
		this.canvasHeight = 256;
		this.canvasPlayButton = null;
		this.canvasTimeCursor = null;
		this.canvasWidth = 1024;
		this.colorChannels = null;
		this.colorDiagram = null;
		this.colorStereoRGB = [null, null];
		this.colorWaveform = null;
		this.drawBuffer = [];
		this.drawEndBuffer = [];
		this.drawMode = 'Combined';
		this.drawScale = 5;
		this.dftCos = null;
		this.dftSin = null;
		this.fftGridData = null;
		this.fftSize = 10;
		this.maxDecibels = -10;
		this.minDecibels = -120;
		this.timeDomainData = [null, null];
	}
	get isAnalyzerMode() {
		return this.drawMode === 'FFT' || this.drawMode === 'Spectrogram' ||
			this.drawMode === 'Generalized spectrogram' || this.drawMode === 'Sound Wave' ||
			this.drawMode === 'DFT (Discrete Fourier Transform)' || this.drawMode === 'Chromagram' ||
			this.drawMode === 'Oscilloscope';
	}
	get timeCursorEnabled() {
		return globalThis.bytebeat.sampleRate >> this.drawScale < 2000;
	}
	clearCanvas() {
		this.canvasCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
		this.canvasCtx.globalCompositeOperation = this.drawMode === 'FFT' ? 'lighter' : 'source-over';
	}
	drawGraphics(endTime) {
		if(!isFinite(endTime)) {
			globalThis.bytebeat.resetTime();
			return;
		}
		if(this.isAnalyzerMode) {
			this.drawAnalyzerGraphics();
			return;
		}
		const buffer = this.drawBuffer;
		const bufferLen = buffer.length;
		if(!bufferLen) {
			return;
		}
		const ctx = this.canvasCtx;
		const width = this.canvasWidth;
		const height = this.canvasHeight;
		const scale = this.drawScale;
		let startTime = buffer[0].t;
		let startX = mod(this.getX(startTime), width);
		let endX = Math.floor(startX + this.getX(endTime - startTime));
		startX = Math.floor(startX);
		let drawWidth = Math.abs(endX - startX) + 1;
		// Truncate large segments (for high playback speed or 512px canvas)
		if(drawWidth > width) {
			startTime = (this.getX(endTime) - width) * (1 << scale);
			startX = mod(this.getX(startTime), width);
			endX = Math.floor(startX + this.getX(endTime - startTime));
			startX = Math.floor(startX);
			drawWidth = Math.abs(endX - startX) + 1;
		}
		startX = Math.min(startX, endX);
		// Restoring the last points of a previous segment
		const imageData = ctx.createImageData(drawWidth, height);
		const { data } = imageData;
		const isReverse = globalThis.bytebeat.playbackSpeed < 0;
		const status = [];
		if(scale) {
			const x = isReverse ? drawWidth - 1 : 0;
			for(let y = 0; y < height; ++y) {
				const drawEndBuffer = this.drawEndBuffer[y];
				if(drawEndBuffer) {
					let idx = drawWidth * (255 - y) + x;
					status[idx] = drawEndBuffer[3];
					idx <<= 2;
					data[idx++] = drawEndBuffer[0];
					data[idx++] = drawEndBuffer[1];
					data[idx] = drawEndBuffer[2];
				}
			}
		}
		// Filling an alpha channel in a segment
		for(let x = 0; x < drawWidth; ++x) {
			for(let y = 0; y < height; ++y) {
				data[((drawWidth * y + x) << 2) + 3] = 255;
			}
		}
		// Drawing in a segment
		const isCombined = this.drawMode === 'Combined';
		const isDiagram = this.drawMode === 'Diagram';
		const isWaveform = this.drawMode === 'Waveform';
		const { colorDiagram } = this;
		const colorCh = this.colorChannels;
		const colorPoints = this.colorWaveform;
		const colorWaveform = [.65 * colorPoints[0] | 0, .65 * colorPoints[1] | 0, .65 * colorPoints[2] | 0];
		let ch, drawPoint;
		for(let i = 0; i < bufferLen; ++i) {
			const curY = toStereo(buffer[i].value);
			const prevY = toStereo(buffer[i - 1]?.value ?? [NaN, NaN]);
			const isNaNCurY = [isNaN(curY[0]), isNaN(curY[1])];
			const curTime = buffer[i].t;
			const nextTime = buffer[i + 1]?.t ?? endTime;
			const curX = mod(Math.floor(this.getX(isReverse ? nextTime + 1 : curTime)) - startX, width);
			const nextX = mod(Math.ceil(this.getX(isReverse ? curTime + 1 : nextTime)) - startX, width);
			let diagramSize, diagramStart;
			if(isCombined || isDiagram) {
				diagramSize = Math.max(1, 256 >> scale);
				diagramStart = diagramSize * mod(curTime, 1 << scale);
			} else if(isNaNCurY[0] || isNaNCurY[1]) {
				// Error value - filling with red color
				for(let x = curX; x !== nextX; x = mod(x + 1, width)) {
					for(let y = 0; y < height; ++y) {
						const idx = (drawWidth * y + x) << 2;
						if(!data[idx + 1] && !data[idx + 2]) {
							data[idx] = 100; // Error: red color
						}
					}
				}
			}
			// Select mono or stereo drawing
			if((curY[0] === curY[1] || isNaNCurY[0] && isNaNCurY[1]) && prevY[0] === prevY[1]) {
				ch = 1;
				drawPoint = this.drawPointMono;
			} else {
				ch = 2;
				drawPoint = this.drawPointStereo;
			}
			while(ch--) {
				const curYCh = curY[ch];
				if(!isNaNCurY[ch] && !isDiagram) {
					// Points drawing
					for(let x = curX; x !== nextX; x = mod(x + 1, width)) {
						const idx = drawWidth * (255 - curYCh) + x;
						status[idx] = 1; // Set the "Point" status
						drawPoint(data, idx << 2, colorPoints, colorCh, ch);
					}
					// Waveform vertical lines drawing
					if(isCombined || isWaveform) {
						const prevYCh = prevY[ch];
						if(isNaN(prevYCh)) {
							continue;
						}
						const x = isReverse ? mod(Math.floor(this.getX(curTime)) - startX, width) : curX;
						for(let dy = prevYCh < curYCh ? 1 : -1, y = prevYCh; y !== curYCh; y += dy) {
							const idx = drawWidth * (255 - y) + x;
							if(status[idx] != 1) { // No "Point" status
								status[idx] = 2; // Set the "Waveform" status
								drawPoint(data, idx << 2, colorWaveform, colorCh, ch);
							}
						}
					}
				}
				// Diagram drawing
				if(isCombined || isDiagram) {
					const isNaNCurYCh = isNaNCurY[ch];
					const value = (curYCh & 255) / 256;
					const color = [
						value * colorDiagram[0] | 0,
						value * colorDiagram[1] | 0,
						value * colorDiagram[2] | 0];
					for(let x = curX; x !== nextX; x = mod(x + 1, width)) {
						for(let y = 0; y < diagramSize; ++y) {
							const idx = drawWidth * (diagramStart + y) + x;
							const s = status[idx];
							if(s != 1 && s != 2) { // No "Point" or "Waveform" status
								if(isNaNCurYCh) {
									data[idx << 2] = 100; // Error: red color
								} else {
									drawPoint(data, idx << 2, color, colorCh, ch);
								}
							}
						}
					}
				}
			}
		}
		// Saving the last points of a segment
		if(scale) {
			const x = isReverse ? 0 : drawWidth - 1;
			for(let y = 0; y < height; ++y) {
				let idx = drawWidth * (255 - y) + x;
				const s = status[idx];
				idx <<= 2;
				this.drawEndBuffer[y] = [data[idx++], data[idx++], data[idx], s];
			}
		}
		// Placing a segment on the canvas
		ctx.putImageData(imageData, startX, 0);
		if(endX >= width) {
			ctx.putImageData(imageData, startX - width, 0);
		} else if(endX <= 0) {
			ctx.putImageData(imageData, startX + width, 0);
		}
		// Move the cursor to the end of the segment
		if(this.timeCursorEnabled) {
			this.canvasTimeCursor.style.left = endX / width * 100 + '%';
		}
		if(['col', '2diagram', '2col', 'heatmap', 'colheatmap', 'Checkerboard', 'Fractal Sticks'].includes(this.drawMode)) {
			this.drawTextureGraphics(buffer);
		}
		// Clear buffer
		this.drawBuffer = [{ t: endTime, value: buffer[bufferLen - 1].value }];
	}
	drawAnalyzerGraphics() {
		switch(this.drawMode) {
		case 'FFT': this.drawFFT(); break;
		case 'Spectrogram': this.drawSpectrogram(false); break;
		case 'Generalized spectrogram': this.drawSpectrogram(true); break;
		case 'Sound Wave': this.drawSoundWave(); break;
		case 'DFT (Discrete Fourier Transform)': this.drawDFT(); break;
		case 'Chromagram': this.drawChromagram(); break;
		case 'Oscilloscope': this.drawOscilloscope(); break;
		}
	}
	drawOscilloscope() {
		if(!this.readTimeDomainData()) {
			return;
		}
		const ctx = this.canvasCtx;
		const width = this.canvasWidth;
		const height = this.canvasHeight;
		ctx.globalCompositeOperation = 'source-over';
		ctx.clearRect(0, 0, width, height);
		ctx.fillStyle = 'rgba(8, 18, 30, .94)';
		ctx.fillRect(0, 0, width, height);
		ctx.strokeStyle = 'rgba(100, 170, 220, .18)';
		ctx.lineWidth = 1;
		ctx.beginPath();
		for(let x = 0; x <= width; x += width / 10) {
			ctx.moveTo(x + .5, 0);
			ctx.lineTo(x + .5, height);
		}
		for(let y = 0; y <= height; y += height / 8) {
			ctx.moveTo(0, y + .5);
			ctx.lineTo(width, y + .5);
		}
		ctx.stroke();
		ctx.strokeStyle = 'rgba(220, 240, 255, .35)';
		ctx.beginPath();
		ctx.moveTo(0, height / 2 + .5);
		ctx.lineTo(width, height / 2 + .5);
		ctx.stroke();
		const left = this.timeDomainData[0];
		const right = this.timeDomainData[1];
		const stereo = left.some((value, index) => value !== right[index]);
		const channels = stereo ? 2 : 1;
		for(let ch = 0; ch < channels; ++ch) {
			const data = this.timeDomainData[ch];
			ctx.strokeStyle = stereo ? this.colorStereoRGB[ch] : `rgb(${ this.colorWaveform.join(',') })`;
			ctx.lineWidth = 1.7;
			ctx.beginPath();
			for(let x = 0; x < width; ++x) {
				const sample = (data[Math.floor(x / Math.max(1, width - 1) * (data.length - 1))] - 128) / 128;
				const y = height / 2 - sample * height * .42;
				x ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
			}
			ctx.stroke();
		}
	}
	drawTextureGraphics(buffer) {
		const ctx = this.canvasCtx;
		const width = this.canvasWidth;
		const height = this.canvasHeight;
		const mode = this.drawMode;
		const samples = buffer.slice(-Math.min(buffer.length, width));
		if(!samples.length) return;
		ctx.globalCompositeOperation = 'source-over';
		ctx.clearRect(0, 0, width, height);
		const valueAt = (sample, channel) => {
			const rawChannel = sample?.value?.length >= 3 && channel === 1 ? 2 : channel;
			const value = sample?.value?.[rawChannel];
			return Number.isFinite(value) ? Math.max(0, Math.min(255, value)) : 0;
		};
		const xAt = index => Math.floor(index / Math.max(1, samples.length - 1) * (width - 1));
		if(mode === 'col' || mode === '2col') {
			for(let i = 0; i < samples.length; ++i) {
				const x = xAt(i);
				const channels = mode === '2col' ? [0, 1] : [0];
				for(const channel of channels) {
					const value = valueAt(samples[i], channel);
					const hue = mode === '2col' ? (channel ? 205 : 330) : 190 + value * .45;
					ctx.fillStyle = `hsl(${ hue }, 90%, ${ 12 + value * .32 }%)`;
					const y = mode === '2col' ? channel * height / 2 : 0;
					const h = mode === '2col' ? height / 2 : height;
					ctx.fillRect(x, y, Math.max(1, Math.ceil(width / samples.length)), h);
				}
			}
			return;
		}
		if(mode === 'heatmap' || mode === 'colheatmap') {
			for(let i = 0; i < samples.length; ++i) {
				const x = xAt(i);
				const value = valueAt(samples[i], 0);
				for(let y = 0; y < height; ++y) {
					const distance = Math.abs(value - 255 * (1 - y / height));
					const intensity = Math.max(0, 1 - distance / 70);
					const hue = mode === 'colheatmap' ? 260 - intensity * 260 : 205;
					ctx.fillStyle = `hsl(${ hue }, 90%, ${ 5 + intensity * 58 }%)`;
					ctx.fillRect(x, y, Math.max(1, Math.ceil(width / samples.length)), 1);
				}
			}
			return;
		}
		if(mode === '2diagram') {
			ctx.strokeStyle = `rgb(${ this.colorDiagram.join(',') })`;
			ctx.lineWidth = 1.5;
			for(const channel of [0, 1]) {
				ctx.beginPath();
				for(let i = 0; i < samples.length; ++i) {
					const x = xAt(i);
					const value = valueAt(samples[i], channel);
					const y = channel * height / 2 + (1 - value / 255) * height / 2;
					i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
				}
				ctx.stroke();
			}
			return;
		}
		if(mode === 'Checkerboard') {
			const cell = Math.max(4, Math.floor(width / samples.length));
			for(let i = 0; i < samples.length; ++i) {
				const value = valueAt(samples[i], 0);
				const x = xAt(i);
				const rows = 8 + (value & 15);
				for(let row = 0; row < rows; ++row) {
					if(((value >> (row & 7)) ^ i ^ row) & 1) {
						ctx.fillStyle = `hsl(${ 190 + (value & 63) }, 75%, ${ 18 + (row % 3) * 10 }%)`;
						ctx.fillRect(x, row * height / rows, cell, height / rows + 1);
					}
				}
			}
			return;
		}
		if(mode === 'Fractal Sticks') {
			ctx.strokeStyle = `rgb(${ this.colorWaveform.join(',') })`;
			ctx.lineWidth = 1;
			for(let i = 0; i < samples.length; ++i) {
				const value = valueAt(samples[i], 0);
				const x = xAt(i);
				let length = value / 255 * height * .8;
				for(let depth = 0; depth < 5 && length > 3; ++depth) {
					const center = height / 2 + Math.sin(i * .7 + depth) * height * .08;
					ctx.globalAlpha = 1 - depth * .14;
					ctx.beginPath();
					ctx.moveTo(x, center - length / 2);
					ctx.lineTo(x, center + length / 2);
					ctx.stroke();
					length *= .57;
				}
			}
			ctx.globalAlpha = 1;
		}
	}
	drawFFT() {
		const ctx = this.canvasCtx;
		const width = this.canvasWidth;
		const height = this.canvasHeight;
		this.clearCanvas();
		const sampleRate = this.getAnalyzerSampleRate();
		const minFreq = Math.max(sampleRate / 2 ** this.fftSize, 10);
		const maxFreq = sampleRate / 2;
			// Grid and labels
			if(this.fftGridData) {
				ctx.putImageData(this.fftGridData, 0, 0);
			} else {
				// Vertical grid and Hz labels
				ctx.beginPath();
				ctx.strokeStyle = '#444';
				ctx.fillStyle = '#faca63';
				ctx.font = '11px monospace';
				let freq = 10; // Start building from 10Hz
				while(freq <= maxFreq) {
					for(let i = 1; i < 10; ++i) {
						const curFreq = freq * i;
						const x = width * Math.log(curFreq / minFreq) / Math.log(maxFreq / minFreq);
						ctx.moveTo(x, 0);
						ctx.lineTo(x, height);
						if(i < 4 || i === 5) {
							ctx.fillText(freq < 1000 ? curFreq + 'Hz' : curFreq / 1000 + 'kHz', x + 1, 10);
						}
					}
					freq *= 10;
				}
				// Horizontal grid and  dB labels
				const dbRange = this.maxDecibels - this.minDecibels;
				for(let i = 10; i <= dbRange; i += 10) {
					const y = i * height / dbRange;
					if(i < dbRange) {
						ctx.moveTo(0, y);
						ctx.lineTo(width, y);
					}
					ctx.fillText(this.maxDecibels - i + 'dB', 1, i * height / dbRange - 2);
				}
				ctx.stroke();
				// Save to the buffer
				this.fftGridData = ctx.getImageData(0, 0, width, height);
			}
			// Detect stereo signal
			let isStereo = false;
			this.readTimeDomainData();
			let i = Math.min(this.timeDomainData[0]?.length || 0, 200);
			while(i--) {
				if(this.timeDomainData[0][i] !== this.timeDomainData[1][i]) {
					isStereo = true;
					break;
				}
			}
			// Build the chart
			let ch = isStereo ? 2 : 1;
			while(ch--) {
				ctx.beginPath();
				ctx.strokeStyle = isStereo ? this.colorStereoRGB[ch] :
					`rgb(${ this.colorWaveform.join(',') })`;
				this.analyser[ch].getByteFrequencyData(this.analyserData[ch]);
				for(let i = 0, len = this.analyserData[ch].length; i < len; ++i) {
					const y = height * (1 - this.analyserData[ch][i] / 256);
					if(i) {
						const ratio = maxFreq / minFreq;
						ctx.lineTo(width * Math.log(i / len * ratio) / Math.log(ratio), y);
						continue;
					}
					ctx.moveTo(0, y);
				}
				ctx.stroke();
			}
	}
	getAnalyzerSampleRate() {
		return this.analyser[0]?.context?.sampleRate || globalThis.bytebeat.audioCtx?.sampleRate || 48000;
	}
	readFrequencyData() {
		if(!this.analyser[0] || !this.analyserData[0]) {
			return false;
		}
		this.analyser[0].getByteFrequencyData(this.analyserData[0]);
		this.analyser[1].getByteFrequencyData(this.analyserData[1]);
		return true;
	}
	readTimeDomainData() {
		if(!this.analyser[0] || !this.timeDomainData[0]) {
			return false;
		}
		this.analyser[0].getByteTimeDomainData(this.timeDomainData[0]);
		this.analyser[1].getByteTimeDomainData(this.timeDomainData[1]);
		return true;
	}
	getSpectrogramColor(value, generalized = false) {
		const intensity = Math.max(0, Math.min(1, value));
		const hue = (generalized ? 300 : 225) * (1 - intensity);
		return `hsl(${ hue }, 90%, ${ 4 + intensity * 52 }%)`;
	}
	scrollAnalyzerCanvas() {
		const ctx = this.canvasCtx;
		ctx.globalCompositeOperation = 'source-over';
		ctx.drawImage(this.canvasElem, -1, 0);
		ctx.clearRect(this.canvasWidth - 1, 0, 1, this.canvasHeight);
	}
	drawSpectrogram(generalized) {
		if(!this.readFrequencyData()) {
			return;
		}
		this.scrollAnalyzerCanvas();
		const height = this.canvasHeight;
		const width = this.canvasWidth;
		const left = this.analyserData[0];
		const right = this.analyserData[1];
		const binMax = left.length - 1;
		const nyquist = this.getAnalyzerSampleRate() / 2;
		const minFreq = Math.min(20, nyquist);
		for(let y = 0; y < height; ++y) {
			const position = 1 - y / Math.max(1, height - 1);
			const bin = generalized ?
				Math.round(minFreq * (nyquist / minFreq) ** position / nyquist * binMax) :
				Math.round(position * binMax);
			const value = Math.max(left[bin], right[bin]) / 255;
			this.canvasCtx.fillStyle = this.getSpectrogramColor(
				generalized ? Math.sqrt(value) : value, generalized);
			this.canvasCtx.fillRect(width - 1, y, 1, 1);
		}
	}
	drawSoundWave() {
		if(!this.readTimeDomainData()) {
			return;
		}
		const ctx = this.canvasCtx;
		const width = this.canvasWidth;
		const height = this.canvasHeight;
		ctx.globalCompositeOperation = 'source-over';
		ctx.clearRect(0, 0, width, height);
		ctx.strokeStyle = 'rgba(128, 128, 128, .28)';
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.moveTo(0, height / 2 + .5);
		ctx.lineTo(width, height / 2 + .5);
		ctx.stroke();
		const isStereo = this.timeDomainData[0].some((value, index) =>
			value !== this.timeDomainData[1][index]);
		const channels = isStereo ? 2 : 1;
		for(let ch = 0; ch < channels; ++ch) {
			const data = this.timeDomainData[ch];
			ctx.strokeStyle = isStereo ? this.colorStereoRGB[ch] :
				`rgb(${ this.colorWaveform.join(',') })`;
			ctx.lineWidth = 1.5;
			ctx.beginPath();
			for(let x = 0; x < width; ++x) {
				const sample = (data[Math.floor(x / Math.max(1, width - 1) * (data.length - 1))] - 128) / 128;
				const y = height / 2 - sample * height * .44;
				if(x) {
					ctx.lineTo(x, y);
				} else {
					ctx.moveTo(x, y);
				}
			}
			ctx.stroke();
		}
	}
	getDFTTables(size) {
		if(this.dftCos?.length === size * size / 2) {
			return;
		}
		const bins = size / 2;
		this.dftCos = new Float32Array(size * bins);
		this.dftSin = new Float32Array(size * bins);
		for(let k = 0; k < bins; ++k) {
			for(let n = 0; n < size; ++n) {
				const angle = 2 * Math.PI * k * n / size;
				this.dftCos[k * size + n] = Math.cos(angle);
				this.dftSin[k * size + n] = Math.sin(angle);
			}
		}
	}
	drawDFT() {
		if(!this.readTimeDomainData()) {
			return;
		}
		const ctx = this.canvasCtx;
		const width = this.canvasWidth;
		const height = this.canvasHeight;
		const size = Math.min(256, this.timeDomainData[0].length);
		const bins = Math.floor(size / 2);
		this.getDFTTables(size);
		const magnitudes = new Float32Array(bins);
		const left = this.timeDomainData[0];
		const right = this.timeDomainData[1];
		const offset = left.length - size;
		for(let k = 0; k < bins; ++k) {
			let real = 0;
			let imaginary = 0;
			for(let n = 0; n < size; ++n) {
				const sample = (((left[offset + n] + right[offset + n]) * .5) - 128) / 128;
				const window = .5 - .5 * Math.cos(2 * Math.PI * n / (size - 1));
				const index = k * size + n;
				real += sample * window * this.dftCos[index];
				imaginary -= sample * window * this.dftSin[index];
			}
			magnitudes[k] = Math.sqrt(real * real + imaginary * imaginary) * 2 / size;
		}
		ctx.globalCompositeOperation = 'source-over';
		ctx.clearRect(0, 0, width, height);
		ctx.strokeStyle = 'rgba(128, 128, 128, .28)';
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.moveTo(0, height - .5);
		ctx.lineTo(width, height - .5);
		ctx.stroke();
		const points = [];
		for(let x = 0; x < width; ++x) {
			const magnitude = magnitudes[Math.min(bins - 1, Math.floor(x / width * bins))];
			const decibels = 20 * Math.log10(Math.max(magnitude, 1e-5));
			const value = Math.max(0, Math.min(1, (decibels + 72) / 72));
			points.push([x, height - value * height * .92]);
		}
		ctx.beginPath();
		points.forEach(([x, y], index) => index ? ctx.lineTo(x, y) : ctx.moveTo(x, y));
		ctx.strokeStyle = `rgb(${ this.colorWaveform.join(',') })`;
		ctx.lineWidth = 1.5;
		ctx.stroke();
		ctx.lineTo(width - 1, height);
		ctx.lineTo(0, height);
		ctx.closePath();
		ctx.fillStyle = 'rgba(64, 128, 255, .16)';
		ctx.fill();
	}
	drawChromagram() {
		if(!this.readFrequencyData()) {
			return;
		}
		this.scrollAnalyzerCanvas();
		const chroma = new Float32Array(12);
		const left = this.analyserData[0];
		const right = this.analyserData[1];
		const sampleRate = this.getAnalyzerSampleRate();
		for(let i = 1; i < left.length; ++i) {
			const frequency = i * sampleRate / (2 * left.length);
			if(frequency < 55 || frequency > 5000) {
				continue;
			}
			const midi = Math.round(69 + 12 * Math.log2(frequency / 440));
			const pitchClass = ((midi % 12) + 12) % 12;
			const magnitude = Math.max(left[i], right[i]) / 255;
			chroma[pitchClass] += magnitude * magnitude;
		}
		let max = 0;
		chroma.forEach(value => { max = Math.max(max, value); });
		const rowHeight = this.canvasHeight / 12;
		for(let row = 0; row < 12; ++row) {
			const pitchClass = 11 - row;
			const value = max ? Math.sqrt(chroma[pitchClass] / max) : 0;
			this.canvasCtx.fillStyle = `hsl(${ pitchClass * 30 }, 85%, ${ 4 + value * 52 }%)`;
			this.canvasCtx.fillRect(this.canvasWidth - 1, row * rowHeight, 1, rowHeight + 1);
		}
	}
	drawPointMono(data, i, color) {
		data[i++] = color[0];
		data[i++] = color[1];
		data[i] = color[2];
	}
	drawPointStereo(data, i, color, colorCh, isRight) {
		for(const channel of colorCh[isRight ? 1 : 0]) {
			data[i + channel] = color[channel];
		}
	}
	getColorTest(colorMode, newValue) {
		if(newValue) {
			this[colorMode] = [
				parseInt(newValue.substr(1, 2), 16),
				parseInt(newValue.substr(3, 2), 16),
				parseInt(newValue.substr(5, 2), 16)];
		}
		const rgbTxt = ['R', 'G', 'B'];
		const value = this[colorMode];
		const [left, right] = this.colorChannels;
		const getColor = channels => {
			const rgb = [0, 0, 0];
			for(const channel of channels) {
				rgb[channel] = value[channel];
			}
			return rgb.join(', ');
		};
		const getLabel = channels => channels.map(channel =>
			`${ rgbTxt[channel] }=${ value[channel] }`).join(' + ');
		return `[ Left <span class="control-color-test" style="background: rgb(${ getColor(left) });"></span>
			${ getLabel(left) }, Right
			<span class="control-color-test" style="background: rgb(${ getColor(right) });"></span>
			${ getLabel(right) } ]`;
	}
	getX(t) {
		return t / (1 << this.drawScale);
	}
	initElements() {
		this.canvasContainer = document.getElementById('canvas-container');
		this.canvasElem = document.getElementById('canvas-main');
		this.canvasCtx = this.canvasElem.getContext('2d');
		this.canvasPlayButton = document.getElementById('canvas-play');
		this.canvasTimeCursor = document.getElementById('canvas-timecursor');
		this.onresizeWindow();
		document.defaultView.addEventListener('resize', () => this.onresizeWindow());
	}
	onresizeWindow() {
		const isSmallWindow = window.innerWidth <= 768 || window.innerHeight <= 768;
		if(this.canvasWidth === 1024) {
			if(isSmallWindow) {
				this.canvasWidth = this.canvasElem.width = 512;
			}
		} else if(!isSmallWindow) {
			this.canvasWidth = this.canvasElem.width = 1024;
		}
	}
	requestAnimationFrame() {
		window.requestAnimationFrame(() => {
			this.drawGraphics(globalThis.bytebeat.byteSample);
			if(globalThis.bytebeat.isPlaying) {
				this.requestAnimationFrame();
			}
		});
	}
	setFFTAnalyzer() {
		this.analyser[0].fftSize = this.analyser[1].fftSize = 2 ** this.fftSize;
		this.analyserData = [
			new Uint8Array(this.analyser[0].frequencyBinCount),
			new Uint8Array(this.analyser[1].frequencyBinCount)];
		this.timeDomainData = [
			new Uint8Array(this.analyser[0].fftSize),
			new Uint8Array(this.analyser[1].fftSize)];
		this.dftCos = null;
		this.dftSin = null;
		this.fftGridData = null;
	}
	setFFTSize(value) {
		this.fftSize = Math.min(Math.max(value, 6), 15);
	}
	setStereoColors() {
		const [left, right] = this.colorChannels;
		const colorLeft = [0, 0, 0];
		const colorRight = [0, 0, 0];
		for(const channel of left) {
			colorLeft[channel] = this.colorWaveform[channel];
		}
		for(const channel of right) {
			colorRight[channel] = this.colorWaveform[channel];
		}
		this.colorStereoRGB = [`rgb(${ colorLeft.join(',') })`, `rgb(${ colorRight.join(',') })`];
	}
	toggleTimeCursor() {
		this.canvasTimeCursor.classList.toggle('hidden', this.isAnalyzerMode || !this.timeCursorEnabled);
	}
}
